import { trackPerformance } from '@/lib/datadog';

export type MfeTimingPhase = 'availability' | 'remoteEntry' | 'load' | 'mount';

function normalizeRoute(route: string): string {
  return route.startsWith('/') ? route.slice(1) : route;
}

function markName(route: string, phase: MfeTimingPhase, edge: 'start' | 'end') {
  return `mfe:${route}:${phase}:${edge}`;
}

function measureName(route: string, phase: MfeTimingPhase) {
  return `mfe:${route}:${phase}:duration`;
}

export function startMfeSpan(route: string, phase: MfeTimingPhase): void {
  try {
    performance.mark(markName(normalizeRoute(route), phase, 'start'));
  } catch (error) {
    console.warn('[mfe-timing] failed to start span', { route, phase, error });
  }
}

export function endMfeSpan(
  route: string,
  phase: MfeTimingPhase,
  extra: Record<string, unknown> = {}
): number | null {
  const cleanRoute = normalizeRoute(route);
  const start = markName(cleanRoute, phase, 'start');
  const end = markName(cleanRoute, phase, 'end');
  const name = measureName(cleanRoute, phase);
  let duration: number | null = null;

  try {
    if (performance.getEntriesByName(start).length === 0) {
      return null;
    }

    performance.mark(end);
    duration = performance.measure(name, start, end).duration;
  } catch (error) {
    console.warn('[mfe-timing] failed to end span', { route, phase, error });
    return null;
  } finally {
    performance.clearMarks(start);
    performance.clearMarks(end);
    performance.clearMeasures(name);
  }

  try {
    trackPerformance(`mfe_${phase}_${cleanRoute}`, duration, {
      category: 'mfe',
      mfe_name: cleanRoute,
      phase,
      route: globalThis.location.pathname,
      ...extra,
    });
  } catch (error) {
    console.warn('[mfe-timing] failed to report span', { route, phase, error });
  }

  return duration;
}
